import React from "react";
import { Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell, Box } from "@mui/material";
// import { Check, Close, WarningAmber } from "@mui/icons-material";
import { List, ListItem, ListItemText } from "@mui/material";

const WhoShouldUseHawkClient = () => {
    return (
        <Box mt={8} display="flex" flexDirection="column" gap={4}>
            {/* Who Should Use HawkClient? */}
            <Box>
                <Typography variant="h6" fontWeight="bold">
                    3. Who Should Use HawkClient?
                </Typography>
                <List>
                    {[
                        "Developers who prefer Git-based workflows",
                        "Teams who need full offline access to API tests",
                        "Privacy-conscious users who don’t want cloud storage",
                        "Anyone looking for a lightweight alternative to Postman",
                    ].map((item, index) => (
                        <ListItem key={index} disableGutters>
                            <ListItemText primary={`• ${item}`} />
                        </ListItem>
                    ))}
                </List>
            </Box>

            {/* Conclusion */}
            <Box>
                <Typography variant="h6" fontWeight="bold">
                    Conclusion
                </Typography>
                <Typography color="textSecondary">
                    If you’re looking for a powerful, offline, Git-friendly API testing tool, HawkClient is the perfect alternative
                    to Postman. With its YAML-based request storage, fast performance, and zero cloud dependencies, it’s built for
                    developers who value control, privacy, and efficiency.
                </Typography>
                <Typography variant="h6" fontWeight="bold" mt={2}>
                    🚀 Switch to HawkClient today and take your API testing to the next level!
                </Typography>
            </Box>
        </Box>
    );
};

const features = [
    { feature: "Offline Mode", hawkClient: "✅ Fully offline", postman: "❌ Requires login for cloud features" },
    { feature: "Storage Format", hawkClient: "✅ YAML files (Git-friendly)", postman: "❌ Proprietary JSON format" },
    { feature: "Git Collaboration", hawkClient: "✅ Easy to track changes in Git", postman: "⚠️ Limited version control" },
    { feature: "Request Storage", hawkClient: "✅ Filesystem-based (easier sharing)", postman: "❌ Cloud-based (requires sync)" },
    { feature: "Performance", hawkClient: "✅ Lightweight and fast", postman: "⚠️ Can be heavy on resources" },
    { feature: "Cost", hawkClient: "✅ Free", postman: "❌ Paid features for advanced usage" },
    { feature: "Automation", hawkClient: "✅ Scriptable in a local environment", postman: "⚠️ Requires cloud or Postman CLI" },
];

const FeatureSections = () => {
    return (
        <Box mt={8} display="flex" flexDirection="column" gap={4}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. Why Choose HawkClient Over Postman?
            </Typography>
            <Box>
                <Typography variant="h6" fontWeight="bold">
                    🔒 100% Offline – No Cloud Dependency
                </Typography>
                <Typography color="textSecondary">
                    HawkClient runs entirely <strong>offline</strong>, ensuring that your API testing workflow is private and
                    secure. Postman, on the other hand, requires an internet connection for many features, including syncing
                    collections.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight="bold">
                    📂 Git-Friendly YAML Storage for Easy Collaboration
                </Typography>
                <Typography color="textSecondary">
                    Unlike Postman’s proprietary JSON format, HawkClient stores requests in{" "}
                    <strong>human-readable YAML files</strong>, making it ideal for <strong>Git collaboration</strong>. This means
                    you can version-control API tests just like code, review changes, and collaborate seamlessly with your team.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight="bold">
                    ⚡ Lightweight & Fast
                </Typography>
                <Typography color="textSecondary">
                    Postman can be resource-heavy, especially for large collections. HawkClient is designed to be{" "}
                    <strong>lightweight and efficient</strong>, making it a great choice for developers who need a fast and
                    responsive tool.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight="bold">
                    🛠️ Better Automation & Scripting
                </Typography>
                <Typography color="textSecondary">
                    With HawkClient, API testing scripts are stored in local files, making it easy to automate requests and
                    integrate with CI/CD pipelines. Postman requires additional cloud-based solutions or its CLI for automation.
                </Typography>
            </Box>

            <Box>
                <Typography variant="h6" fontWeight="bold">
                    💰 No Hidden Costs
                </Typography>
                <Typography color="textSecondary">
                    While Postman offers a free plan, its advanced features (such as collaboration and automation) require paid
                    subscriptions. HawkClient is <strong>completely free</strong> and designed for developers who want full
                    control over their API testing without recurring costs.
                </Typography>
            </Box>
        </Box>
    );
};

const PostManComparison = () => {
    return (
        <Box sx={{ padding: '48px' }}>
            <Typography variant="h4" align="center" gutterBottom>
                HawkClient vs. Postman
            </Typography>
            <Typography variant="h6" align="center" gutterBottom>
                HawkClient: The Best API Testing Tool for Offline and Git-Based Collaboration
            </Typography>
            <Typography variant="body1" align="center" color="textSecondary" paragraph>
                When it comes to API testing, Postman is the most well-known tool, but it has limitations, especially for developers who prefer offline work and Git-based collaboration. HawkClient offers a powerful alternative designed for developers who value privacy, version control, and seamless offline usage.
            </Typography>

            <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. HawkClient vs. Postman: Key Differences
            </Typography>
            <Card>
                <CardContent sx={{ overflow: 'scroll' }}>
                    <Table sx={{ overflow: 'scroll' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Feature</strong></TableCell>
                                <TableCell><strong>HawkClient</strong></TableCell>
                                <TableCell><strong>Postman</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {features.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell>{row.feature}</TableCell>
                                    <TableCell> {row.hawkClient}</TableCell>
                                    <TableCell> {row.postman}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <FeatureSections />

            <WhoShouldUseHawkClient />
            {/* <div style={{ display: "flex", justifyContent: "center", marginTop: "16px" }}>
                <Button variant="contained" color="primary">
                    Try HawkClient Now
                </Button>
            </div> */}
        </Box>
    );
};

export default PostManComparison;